import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { PendoService } from 'analytics-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { IFile } from 'interfaces-lib';
import { WorkflowMessage } from 'interfaces-lib';
import { ILlm, LlmConfigService } from 'llm-config';
import { MessageNotificationService } from 'message-notification-service-lib';
import { firstValueFrom, forkJoin, Observable, Subject } from 'rxjs';
import { ContentTypeMapperService, FileExtensionMatcherService } from 'utils-service-lib';
import { environment } from '../../../environments/environment';
import { MESSAGE_TYPE } from '../../classes/ai-response-task';
import { AiUtil } from '../../classes/aiUtil';
import { BullhornEntityCollection } from '../../classes/bullhornEntityCollection';
import { StringUtils } from '../../classes/stringUtils';
import { AiPromptService } from '../../services/ai-prompt.service';
import { BullhornDataService } from '../../services/bullhorn-data.service';
import { BullhornMetaService, IEntityLabelPair } from '../../services/bullhorn-meta.service';
import { BullhornIconService } from '../../services/bullhorn.icon.service';
import { ConfigService } from '../../services/config.service';
import { PromptProfileService } from '../../services/prompt-profile.service';
import { AIApps } from '../../types/AIApps';
import { IBullhornCardContext } from '../../types/bullhorn-card-context';
import { BullhornEntityType } from '../../types/bullhorn/bullhorn-entity-type';
import { IJobSubmission } from '../../types/bullhorn/job-submission';
import { INote } from '../../types/bullhorn/note';
import { IClientConfig } from '../../types/client-config';
import { IMessageHandlerComponent } from '../../types/component-method-message-handler';
import { IAiModel } from '../../types/IAiModel';
import { IEntitySourceData } from '../../types/IEntitySourceData';
import { getIPromptOptionsKeys, IPromptOptions } from '../../types/IPromptProfileMeta';
import { IFilter, IPromptProfile, SortFilterType } from '../../types/prompt-profile';
import { ConfigurePromptComponent } from './configure-prompt/configure-prompt.component';

declare let pendo: any;

@Component({
  selector: 'app-bh-card',
  templateUrl: './bh-card.component.html',
  styleUrls: ['./bh-card.component.scss', '../prompt-carousel/prompt-carousel.scss'],
  standalone: false,
})
export class BhCardComponent implements OnInit, IMessageHandlerComponent {
  protected readonly appVersion;
  protected expandVersion = false;

  protected clientConfig!: IClientConfig;
  protected availablePromptProfiles: IPromptProfile[] = [];
  protected bullhornCardContext!: IBullhornCardContext;

  protected readonly environment = environment;

  protected clientConfigLoaded = false;
  protected loadingPrompts = true;
  protected loadingResponse = false;

  protected selectedPromptProfile!: IPromptProfile | undefined;
  protected defaultSelected = {
    role: '',
    tone: '',
    format: '',
    resultLength: '',
    explanationDifficulty: '',
    outputLanguage: '',
  };
  protected aiResponse: string | null = null;
  protected enableCopyButton = true;
  protected enableSaveAsNote = true;
  private aiTokenError = 'No token count found.';
  protected aiTokenResponse: string = this.aiTokenError;
  protected fieldNameToSaveTo: string | undefined = this.selectedPromptProfile?.outputFieldName;

  protected selectedModel = '';
  protected selectedTemperature = 0;

  protected aiGenerationProgress$ = new Subject<string>();

  protected hasRunYet = false;

  protected notes: INote[] = [];
  protected selectedNotes: INote[] = [];
  protected files: IFile[] = [];
  protected selectedFiles: IFile[] = [];
  private static MAX_BASKET_SIZE_MB = 50;
  private static MAX_BASKET_SIZE_BYTES: number = 1024 * 1024 * BhCardComponent.MAX_BASKET_SIZE_MB;
  totalFileSizeBytes = 0;

  public availableDataV7: IEntitySourceData[] = [];
  public additionalEntityData = new BullhornEntityCollection();

  protected fileDataOnly = false;

  protected selectedTab = 0;
  protected previousTab = 0;
  protected hideMatTabs = false;
  protected currentSlideId = 0;
  protected promptSlides: IPromptProfile[][] = [];

  protected disableContainerTooltip = false;

  protected aiModels: string[] = [];
  protected promptOptions!: IPromptOptions;
  entities: IEntityLabelPair[] = [];
  private llmList: ILlm[] = [];

  private static readonly BASE_ALLOWED_EXTENSIONS = ['doc', 'docx', 'pdf', 'txt'];
  private static readonly EXPANDED_ALLOWED_EXTENSIONS = ['odt', 'rtf', 'md'];

  private static readonly submissionRelations: Partial<
    Record<BullhornEntityType, { active: string; related: string }>
  > = {
    [BullhornEntityType.CANDIDATE]: { active: 'candidate', related: 'jobOrder' },
    [BullhornEntityType.JOB_ORDER]: { active: 'jobOrder', related: 'candidate' },
  };

  @ViewChild('slider') private slider: ElementRef | undefined;
  @ViewChild('configurePromptComponent') configurePromptComponent!: ConfigurePromptComponent;
  @ViewChildren('slideArray') slideArray: QueryList<ElementRef> | undefined;

  constructor(
    protected router: Router,
    private bullhornMetaService: BullhornMetaService,
    private bullhornDataService: BullhornDataService,
    private clientConfigService: ConfigService,
    private aiPromptService: AiPromptService,
    private messageNotificationService: MessageNotificationService,
    private clipboard: Clipboard,
    private pendoService: PendoService,
    private promptProfileService: PromptProfileService,
    protected bullhornIconService: BullhornIconService,
    private fileExtensionMatcherService: FileExtensionMatcherService,
    private contentTypeMapperService: ContentTypeMapperService,
    private llmConfigService: LlmConfigService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.appVersion = environment.version;
  }

  private get allowedExtensions(): string[] {
    return this.featureFlagService.enabled(FeatureFlagService.FLAGS.aiaExpandedFileFormats)()
      ? [...BhCardComponent.BASE_ALLOWED_EXTENSIONS, ...BhCardComponent.EXPANDED_ALLOWED_EXTENSIONS]
      : BhCardComponent.BASE_ALLOWED_EXTENSIONS;
  }

  /**
   * Resolves stored LLM values to display names.
   * Handles legacy friendly names ('gpt-4-turbo') and Azure/Bedrock deployment IDs ('kyloe-ai-gpt4').
   * Uses the centralized replacement resolver to handle replacement chains.
   *
   * @param storedLLMs Array of model identifiers from database
   * @returns Array of display names for UI
   */
  private resolveModelsToNames(storedLLMs: string[]): string[] {
    if (!this.llmList || this.llmList.length === 0) {
      console.warn('LLM list not loaded yet. Returning stored values temporarily.');
      return storedLLMs;
    }

    const resolvedNames: string[] = [];

    for (const storedValue of storedLLMs) {
      const migratedValue = this.promptProfileService.updateLLMs([storedValue])[0];

      let model = this.llmList.find((m) => m.modelId === migratedValue);

      if (!model) {
        const aiModel = AiUtil.getModelByName(migratedValue);
        if (aiModel) {
          model = this.llmList.find((m) => m.modelId === aiModel.model);
        }
      }

      if (model) {
        const resolvedModel = this.llmConfigService.resolveLlmReplacement(
          model.modelId,
          this.llmList,
        );

        if (resolvedModel) {
          resolvedNames.push(resolvedModel.name);
          if (resolvedModel.modelId !== model.modelId) {
            console.debug(`Resolved deprecated model ${storedValue} to ${resolvedModel.name}`);
          }
        } else {
          console.warn(`Could not resolve replacement for model: ${storedValue}`);
        }
      } else {
        console.warn(
          `Could not resolve model: ${storedValue} (after migration: ${migratedValue}). Skipping.`,
        );
      }
    }

    return [...new Set(resolvedNames)];
  }

  ngOnInit(): void {
    this.getBHCardContext();
    this.getClientConfig();
    this.loadPendo();
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 0) {
      this.currentSlideId = 0;
      this.files = [];
      this.selectedFiles = [];
      this.notes = [];
      this.selectedNotes = [];
    }
    if (this.previousTab === 2 && event.index === 1) {
      this.applyFilter(
        this.selectedPromptProfile?.noteFilter,
        this.setFilteredNotes.bind(this),
        this.notes.length,
      );
      this.applyFilter(
        this.selectedPromptProfile?.fileFilter,
        this.setFilteredFiles.bind(this),
        this.files.length,
      );
    }
    this.previousTab = event.index;
  }

  protected buildPromptSlidesGrid(array: IPromptProfile[], size: number): void {
    let index = 0;
    while (index < array.length) {
      this.promptSlides!.push(array.slice(index, size + index));
      index += size;
    }
  }

  handleNext() {
    if (this.currentSlideId >= this.promptSlides!.length - 1) {
      this.currentSlideId = 0;
    } else {
      this.currentSlideId++;
    }

    this.setCurrent();
  }

  handlePrevious() {
    if (this.currentSlideId === 0) {
      this.currentSlideId = this.promptSlides!.length - 1;
    } else {
      this.currentSlideId--;
    }

    this.setCurrent();
  }

  handleSlideIndicatorClick(index: number) {
    this.currentSlideId = index;
    this.setCurrent();
  }

  protected setPromptProfiles(additionalEntity?: BullhornEntityType): void {
    console.debug('Setting Prompt Profiles');

    const entityPrompts$ = [
      this.clientConfigService.getPromptsByEntity(this.clientConfig, this.activeBHEntity),
      ...(additionalEntity
        ? [this.clientConfigService.getPromptsByEntity(this.clientConfig, additionalEntity)]
        : []),
    ];

    forkJoin(entityPrompts$).subscribe((prompts) => {
      this.availablePromptProfiles = this.clientConfigService.sortPrompts(
        prompts.flat(),
        this.clientConfig,
      );
      console.debug('Available Prompt Profiles: ', this.availablePromptProfiles);
      this.buildPromptSlidesGrid(this.availablePromptProfiles, 9);
      this.loadingPrompts = false;
    });
  }

  protected setSelectedNotes($event: { notesSelected: INote[] }) {
    this.selectedNotes = $event.notesSelected;
  }

  protected setSelectedFiles($event: { filesSelected: IFile[] }) {
    const files: IFile[] = $event.filesSelected;
    this.totalFileSizeBytes = this.totalFileSize(files);
    if (this.totalFileSizeBytes <= BhCardComponent.MAX_BASKET_SIZE_BYTES) {
      this.selectedFiles = $event.filesSelected;
    } else {
      this.messageNotificationService.error(
        `The selected files exceed the maximum size of ${BhCardComponent.MAX_BASKET_SIZE_MB} MB.  Please reduce the number of files selected`,
      );
    }
  }

  useFileData($event: { useEntityData: boolean }) {
    this.fileDataOnly = $event.useEntityData;
  }

  configurePrompt(prompt: IPromptProfile, event?: any): void {
    if (prompt.LLMs.length === 0) {
      this.messageNotificationService.error('This prompt has no LLMs selected.');
      return;
    }

    if (event) {
      event.stopPropagation();
    }
    this.applyFilter(prompt.noteFilter, this.setFilteredNotes.bind(this));
    this.applyFilter(prompt.fileFilter, this.setFilteredFiles.bind(this));

    this.selectedPromptProfile = prompt;
    if (prompt.LLMs) {
      this.aiModels = this.resolveModelsToNames(prompt.LLMs);

      if (this.aiModels.length === 0) {
        this.messageNotificationService.error(
          'This prompt has no valid LLMs available.',
          'The configured models are no longer supported. Please update this prompt in the AI Assist Manager.',
        );
        return;
      }
    } else {
      this.aiModels.push(this.selectedModel);
    }
    this.selectedModel = this.aiModels[0];
    this.loadPromptOptions(prompt);
    getIPromptOptionsKeys().forEach((key) => {
      this.defaultSelected[key] = this.promptOptions[key][0];
    });
    this.selectedTab = 1;

    this.configurePromptComponent?.clearSelections();
  }

  protected async sendPromptRequest(promptProfile: IPromptProfile, options?: any): Promise<void> {
    console.log('sending', promptProfile, this.selectedPromptProfile);
    if (promptProfile.LLMs.length === 0) {
      this.messageNotificationService.error('This prompt has no LLMs selected.');
    } else {
      this.hasRunYet = false; // Reset
      this.aiResponse = null;
      this.aiTokenResponse = this.aiTokenError;
      const resolvedNames = this.resolveModelsToNames([promptProfile.LLMs[0]]);
      this.selectedModel = resolvedNames.length > 0 ? resolvedNames[0] : '';
      this.loadPromptOptions(promptProfile);
      if (
        promptProfile.entityType === BullhornEntityType.JOB_SUBMISSION &&
        this.selectedTab !== 1
      ) {
        this.configurePrompt(promptProfile);
      } else {
        // Cannot use the applyFilter method as this must be asynchronous
        const processFilter = async (
          length: number,
          filter: any,
          filterFn: (filter: any) => Promise<void>,
        ) => {
          if (filter && filter.isFilterEnabled !== false && filter.autoInclude && length === 0) {
            await filterFn(filter);
          }
        };
        await processFilter(
          this.notes.length,
          promptProfile.noteFilter,
          this.setFilteredNotes.bind(this),
        );
        await processFilter(
          this.files.length,
          promptProfile.fileFilter,
          this.setFilteredFiles.bind(this),
        );

        if (options) {
          this.updatePromptOptions(options);
        } else {
          this.setCurrentPromptProfile(promptProfile);
        }
        this.hasRunYet = true;
        this.sendAiRequest();
      }
      this.setCurrentPromptProfile(promptProfile);
    }
    this.sendPromptRequestPendoTrackEvent(promptProfile, options);
  }

  private sendPromptRequestPendoTrackEvent(promptProfile: IPromptProfile, options?: any) {
    const configSource = options ? options.promptProfile : promptProfile;
    const eventTitle = options ? 'Run prompt from Config screen' : 'Run prompt';

    const formatVal = (val: string | string[] | undefined): string => {
      const value = Array.isArray(val) ? val[0] : val;
      return value || '';
    };

    this.pendoService.recordEvent(eventTitle, {
      promptName: promptProfile.displayName,
      action: promptProfile.action
        ? promptProfile.action.length > 70
          ? promptProfile.action.slice(0, 70) + '…'
          : promptProfile.action
        : '',
      entityType: promptProfile.entityType,
      additionalContext: promptProfile.additionalContext
        ? promptProfile.additionalContext.length > 50
          ? promptProfile.additionalContext.slice(0, 50) + '…'
          : promptProfile.additionalContext
        : '',

      LLMUsed: options?.selectedModel ? options.selectedModel : promptProfile.LLMs[0],
      anonymise: !!configSource.anonymise,
      numberOfFiles: this.selectedFiles.length,
      fileDataOnly: this.fileDataOnly,
      numberOfNotes: configSource.noteFilter?.category?.length ?? 0,

      role: formatVal(configSource.role),
      tone: formatVal(configSource.tone),
      format: formatVal(configSource.format),
      resultLength: formatVal(configSource.resultLength),
      explanationDifficulty: formatVal(configSource.explanationDifficulty),
      outputLanguage: formatVal(configSource.outputLanguage),
      enableCopyButton: promptProfile.enableCopyButton ?? true,
      enableSaveAsNote: promptProfile.enableSaveAsNote ?? true,
    });

    this.pendoService.recordEvent('Prompt | Additional Context', {
      promptName: promptProfile.displayName,
      additionalContext: promptProfile.additionalContext
        ? promptProfile.additionalContext.length > 400
          ? promptProfile.additionalContext.slice(0, 400) + '…'
          : promptProfile.additionalContext
        : '',
      enableCopyButton: promptProfile.enableCopyButton ?? true,
      enableSaveAsNote: promptProfile.enableSaveAsNote ?? true,
    });

    this.pendoService.recordEvent('Prompt | Action', {
      promptName: promptProfile.displayName,
      action: promptProfile.action
        ? promptProfile.action.length > 400
          ? promptProfile.action.slice(0, 400) + '…'
          : promptProfile.action
        : '',
      enableCopyButton: promptProfile.enableCopyButton ?? true,
      enableSaveAsNote: promptProfile.enableSaveAsNote ?? true,
    });
  }

  protected sendAiRequest(): void {
    this.selectedTab = 2;
    this.loadingResponse = true;
    const llmModel = this.llmList.find((m) => m.name === this.selectedModel);

    if (!llmModel) {
      this.loadingResponse = false;
      this.selectedTab = 1;
      this.messageNotificationService.error(
        'This AI model is no longer available.',
        `Please select a different model or update the prompt in the AI Assist Manager`,
      );
      return;
    }
    const aiModel: IAiModel = { provider: llmModel.provider, model: llmModel.modelId } as IAiModel;

    this.aiPromptService.sendAiRequest(
      this.availableDataV7,
      this.promptProfileService.createPromptRequest(this.selectedPromptProfile!),
      {
        aiProvider: aiModel.provider,
        aiModel: aiModel.model,
        temperature: this.selectedTemperature,
      },
      this.selectedFiles,
      this.fileDataOnly,
      this.notesToSourceObject(),
      this.bullhornCardContext,
      this,
    );
  }

  protected setCurrentPromptProfile(promptProfile: IPromptProfile): void {
    console.debug('Setting current Prompt Profile', promptProfile);
    promptProfile.sourceFields = this.bullhornDataService.updateSourceData(
      promptProfile.sourceFields,
    );
    this.availableDataV7.forEach((entityData) => {
      entityData.fields = promptProfile.sourceFields;
    });
    this.selectedPromptProfile = promptProfile;
    this.enableCopyButton = promptProfile.enableCopyButton ?? true;
    this.enableSaveAsNote = promptProfile.enableSaveAsNote ?? true;
  }

  protected copyToClipboard(aString: string): void {
    this.clipboard.copy(aString);
    this.messageNotificationService.success(`Copied ${aString} to clipboard`);
  }

  public getEntityLabel(entityType: BullhornEntityType): string {
    const entity = this.entities.find((entity) => entity.objectName === entityType);
    return entity ? entity.label : entityType;
  }

  private loadPromptOptions(prompt: IPromptProfile) {
    this.promptOptions = {
      role: [],
      tone: [],
      format: [],
      resultLength: [],
      explanationDifficulty: [],
      outputLanguage: [],
    };
    // ToDo move all getIPromptOptionsKeys into one function
    getIPromptOptionsKeys().forEach((key) => {
      if (key in prompt) {
        if (Array.isArray(prompt[key])) {
          this.promptOptions[key] = prompt[key].map((item: string) =>
            item === '<blank>' ? '' : item,
          );
        } else {
          this.promptOptions[key] = [prompt[key]];
        }
      }
      this.promptOptions[key] = this.promptOptions[key].filter((item: string) =>
        this.clientConfig.promptProfileMeta?.options[key].includes(item),
      );
    });
  }

  private setCurrent(): void {
    setTimeout(() => {
      // Necessary for rapidly navigating the carousel with the keyboard
      // this.selectedPrompt.emit(this.availablePromptProfiles[this.current]);
      this.scrollTo();
    }, 0);
  }

  private scrollTo() {
    this.slideArray?.forEach((ulSlide) => {
      if (ulSlide.nativeElement.id === this.currentSlideId.toString()) {
        ulSlide.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
    this.slider!.nativeElement.focus();
  }

  private notesToSourceObject(): IEntitySourceData[] {
    if (this.selectedNotes && this.selectedNotes.length !== 0) {
      const noteData: IEntitySourceData[] = [];
      this.selectedNotes.forEach((note) => {
        if (note.id) {
          noteData.push(this.bullhornDataService.getEntityByIdV7(BullhornEntityType.NOTE, note.id));
        }
      });
      return noteData;
    }
    return [];
  }

  private applyFilter(filter: any, filterFn: (filter: any) => Promise<void>, length?: number) {
    if (
      (length === undefined || length === 0) &&
      (filter === undefined || filter?.isFilterEnabled !== false)
    ) {
      filterFn(filter).then();
    }
  }

  protected async setFilteredNotes(noteFilter: IFilter | undefined): Promise<void> {
    const entity = {
      entity: {
        type: this.bullhornCardContext.cardEntityType,
        id: this.bullhornCardContext.cardEntityId.toString(),
      },
    };
    const response = await firstValueFrom(
      this.bullhornDataService.filteredListNotes({ ...entity }, noteFilter),
    );
    this.notes = response.map((item) => ({
      id: item.id,
      action: item.action,
      dateLastModified: item.dateLastModified,
    }));
    if (noteFilter?.autoInclude) {
      this.selectedNotes = this.notes;
    }
  }

  private async setFilteredFiles(fileFilter: IFilter | undefined): Promise<void> {
    this.files = [];
    // LEADS DO NOT HAVE FILES
    //
    if (this.bullhornCardContext.cardEntityType !== BullhornEntityType.LEAD) {
      const entity = {
        type: this.bullhornCardContext.cardEntityType,
        id: this.bullhornCardContext.cardEntityId.toString(),
      };
      const fields = 'fileAttachments';
      this.selectedFiles = [];

      const response = await firstValueFrom(
        this.bullhornDataService.listFilteredFiles(
          {entity, fields},
          fileFilter,
          this.allowedExtensions,
        ),
      );

      const allFiles: any[] = [];
      response.forEach((item) => {
        item.data.forEach((file: any) => {
          allFiles.push(file);
        });
      });

      const normalisedFiles = allFiles.map((file) =>
        this.contentTypeMapperService.normaliseFileExtension(file),
      );

      const acceptedFiles = normalisedFiles.filter((file) => {
        return this.fileExtensionMatcherService.matchesAllowedExtension(
          file,
          this.allowedExtensions,
        );
      });

      this.files = acceptedFiles.map((item) => ({
        id: item.id,
        type: item.type,
        name: item.name,
        fileSize: item.fileSize,
        dateModified: item.dateModified,
        fileExtension: item.fileExtension,
        contentSubType: item.contentSubType,
      }));

      if (fileFilter?.sort === SortFilterType.OLDEST) {
        this.files.reverse();
      }
      if (fileFilter?.autoInclude) {
        this.setSelectedFiles({ filesSelected: this.files });
      }
    }
  }

  private totalFileSize(files: IFile[]): number {
    let totalFileSize = 0;
    for (let i = 0; i < files.length; i++) {
      const file: IFile = files[i];
      totalFileSize += file.size ? file.size : 0;
    }
    return totalFileSize;
  }

  // ToDo: This method should be similar to the NotetakerCardComponent, where the card context is set
  // ToDo: via the bullhorn auth service class.
  private getBHCardContext(): void {
    const params = new URLSearchParams(window.location.search);

    this.bullhornCardContext = {
      corporationId: `bullhorn-${Number(params.get('CorporationID')).toString()}`,
      userId: Number(params.get('UserID')),
      cardEntityId: Number(params.get('EntityID')),
      cardEntityType: this.bullhornMetaService.toBullhornEntity(
        params.get('EntityType') as BullhornEntityType,
      ),
    };
    console.log('Set bullhornCardContext', this.bullhornCardContext);
  }

  private getClientConfig(): void {
    const config$: Observable<IClientConfig> = this.clientConfigService.getFullConfigForCorpId(
      AIApps.ASSIST,
      environment.apiGatewayUrl,
      environment.dtApiGatewayUrl,
      this.bullhornCardContext.corporationId,
    );
    const obs$: Observable<any>[] = [config$];
    const needsJobSubmission = this.doesNeedJobSubmission();
    if (needsJobSubmission) {
      const submissionRelation = BhCardComponent.submissionRelations[this.activeBHEntity];
      let queryWhere = ''
      if (submissionRelation) {
        queryWhere = `${submissionRelation.active}.id=${this.bullhornCardContext.cardEntityId} AND ${submissionRelation.related}.isDeleted=false AND ${submissionRelation.related}.status<>'Archive'`;
      }
      const jobSubmission$: Observable<IJobSubmission[]> =
        this.bullhornDataService.getJobSubmissions(queryWhere);
      obs$.push(jobSubmission$);
    }

    forkJoin(obs$).subscribe({
      next: (result) => {
        this.clientConfig = result[0] as IClientConfig;
        this.clientConfig.promptProfileMeta!.options = this.mapGlobalPromptOptions(
          this.clientConfig.promptProfileMeta!.options,
        );

        if (!this.clientConfig.enabled) {
          this.messageNotificationService.error('Client is not enabled');
          return;
        }

        this.llmConfigService
          .getLlmModels(this.clientConfig.enabledModels ?? [])
          .subscribe((llms) => (this.llmList = llms));

        this.clientConfigLoaded = true;
        this.availableDataV7.push(
          this.bullhornDataService.getEntityByIdV7(
            this.activeBHEntity,
            this.bullhornCardContext.cardEntityId as number,
          ),
        );
        if (result.length > 1) {
          this.additionalEntityData.jobSubmissions = result[1] as IJobSubmission[];
        }
        let additionalEntity: BullhornEntityType | undefined;
        if (needsJobSubmission && this.additionalEntityData.jobSubmissions.length !== 0) {
          additionalEntity = BullhornEntityType.JOB_SUBMISSION;
        }
        this.bullhornMetaService.getAllEntityMeta().subscribe({
          next: () => {
            this.entities = this.bullhornMetaService.getLabelPairs();
            this.setPromptProfiles(additionalEntity);
          },
        });
      },
      // ToDo: merge with code in notetaker card
      error: (error) => {
        console.log(error);
        if (error.status && error.status === 401) {
          this.messageNotificationService.error('Client is not enabled');
        } else {
          this.messageNotificationService.error('Failed to load client config', error);
        }
      },
    });
  }

  private get activeBHEntity(): BullhornEntityType {
    let entity: BullhornEntityType = this.bullhornCardContext.cardEntityType;

    if (this.selectedPromptProfile?.entityType === BullhornEntityType.JOB_SUBMISSION) {
      entity = BullhornEntityType.JOB_SUBMISSION;
    }

    return entity;
  }

  private doesNeedJobSubmission(): boolean {
    return (
      this.bullhornCardContext.cardEntityType === BullhornEntityType.CANDIDATE ||
      this.bullhornCardContext.cardEntityType === BullhornEntityType.JOB_SUBMISSION ||
      this.bullhornCardContext.cardEntityType === BullhornEntityType.JOB_ORDER
    );
  }

  private updatePromptOptions(options: any) {
    if (options.selectedModel) {
      this.selectedModel = options.selectedModel;
    }
    if (options.promptProfile) {
      this.setCurrentPromptProfile(options.promptProfile);
    }
    if (options.jobSubmission.length > 0) {
      this.mapJobSubmissions(options.jobSubmission);
    }
  }

  private loadPendo() {
    this.pendoService.initPendoScript(environment.aiaBhCardPendoAPIKey);
  }

  private mapJobSubmissions(jobSubmissions: IJobSubmission[]) {
    this.availableDataV7 = [];
    let entityData: IEntitySourceData;
    jobSubmissions.forEach((entity) => {
      if (entity.candidate) {
        entityData = this.bullhornDataService.getEntityByIdV7(
          BullhornEntityType.CANDIDATE,
          entity.candidate.id!,
          this.selectedPromptProfile!.sourceFields,
        );
        this.availableDataV7.push(entityData);
      }
      if (entity.jobOrder) {
        entityData = this.bullhornDataService.getEntityByIdV7(
          BullhornEntityType.JOB_ORDER,
          entity.jobOrder.id!,
          this.selectedPromptProfile!.sourceFields,
        );
        this.availableDataV7.push(entityData);
      }
    });
  }

  private mapGlobalPromptOptions(globalPromptOptions: IPromptOptions) {
    const copyPromptOptions: IPromptOptions = { ...globalPromptOptions };
    for (const key in copyPromptOptions) {
      const aKey = key as keyof IPromptOptions;
      if (Array.isArray(copyPromptOptions[aKey])) {
        copyPromptOptions[aKey] = copyPromptOptions[aKey].map((item: string) =>
          item === '<blank>' ? '' : item,
        );
      }
    }
    return copyPromptOptions;
  }

  private handleAiResponse(response: string) {
    this.aiResponse = StringUtils.formatForHtml(response);
    this.fieldNameToSaveTo = this.selectedPromptProfile?.outputFieldName ?? undefined;
    this.loadingResponse = false;
  }

  public handleMessage(message: WorkflowMessage): void {
    if (message.type === MESSAGE_TYPE.AI_SERVICE_RESPONSE) {
      this.handleAiResponse(message.message.message);
      this.aiTokenResponse = message.message.tokensUsed;
    } else {
      this.aiGenerationProgress$.next(message.message.status);
    }
  }

  public handleError(message: WorkflowMessage): void {
    this.loadingResponse = false;
    this.messageNotificationService.error('Error', message.message);
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + V to enlarge version number
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyV') {
        this.expandVersion = true;
        setTimeout(() => {
          this.expandVersion = false;
        }, 3000);
      }
    }
  }

  handleQuestion(message: WorkflowMessage): void {}
}
